'use client'

import { useMemo } from 'react'

import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  type WatchlistDropdownItem,
  type WatchlistStatusVariant,
} from '@b-stock/fe-core/bstock-react'
import {
  AllListingsSortByEnum,
  AllListingsSortOrderEnum,
} from '@b-stock/search-api-client'

import { defaultState } from '@/components/listings/search/ListingSearchProvider/defaultState'
import { listingSearchStateToSearchParams } from '@/components/listings/search/ListingSearchProvider/utils'
import { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import listingSearchQuery, {
  type SearchResult,
} from '@/queries/listingSearchQuery'
import { allWatchedListingsQuery } from '@/queries/watchlistQuery'

import { WatchlistItemPrice } from './WatchlistItemPrice'

// How many watchlist items to show in the header dropdown. The badge still
// reflects the full watched count; "See all" links to the watchlist page.
const MAX_DISPLAYED_ITEMS = 3

const STATUS_BY_BID: Partial<Record<BidStatus, WatchlistStatusVariant>> = {
  [BidStatus.WINNING]: 'winning',
  [BidStatus.LOSING]: 'losing',
  [BidStatus.NO_BID]: 'bidNow',
}

// Buy Now replaces Bid Now only while Buy Now is the sole way to buy; none
// before purchasing opens or once it ends.
export const getStatusVariant = (
  listing: SearchResult
): WatchlistStatusVariant | undefined => {
  const bidStatus =
    listing.auction3MP?.bidStatus ?? listing.auctionEnt?.bidStatus
  const variant = bidStatus ? STATUS_BY_BID[bidStatus] : undefined
  if (variant !== 'bidNow') return variant
  const phase = new CombinedListingSearchResult(listing).activePurchasingPhase
  return phase === 'BINO' ? 'buyNow' : phase ? 'bidNow' : undefined
}

const toDropdownItem = (listing: SearchResult): WatchlistDropdownItem => {
  const variant = getStatusVariant(listing)
  const status = variant
    ? {
        label: (
          <FormattedMessage id={`GlobalHeader.watchlist.status.${variant}`} />
        ),
        variant,
      }
    : undefined

  return {
    id: listing.id || listing.listingId,
    title: listing.title,
    subtitle: (
      <>
        <WatchlistItemPrice listing={listing} />
        {listing.storefrontName ? ` | ${listing.storefrontName}` : ''}
      </>
    ),
    imageUrl:
      listing.primaryImageUrl || '/home-portal/images/fallbackListingImage.png',
    ...(status ? { status } : {}),
    href: `/buy/listings/details/${listing.listingId}`,
  }
}

type WatchlistDropdownData = {
  items: WatchlistDropdownItem[]
  seeAllHref: string
  isLoading: boolean
}

/**
 * Adapts the buyer's watchlist into fe-core `WatchlistDropdownItem`s.
 *
 * Two-step fetch (mirrors `PrefetchedWatchlistCarousel`): the watchlist endpoint
 * returns listing IDs, which we feed to the listing-search query to get titles,
 * prices, sellers, images, and the per-listing `bidStatus` used for the status
 * pill. Both queries are gated on `personId`, so logged-out users fire nothing.
 */
export function useWatchlistDropdownData(
  personId: string | undefined
): WatchlistDropdownData {
  const { data: watchlistEntries = [], isLoading: isLoadingEntries } =
    allWatchedListingsQuery.useWithOpts([personId ?? ''], {
      enabled: !!personId,
    })

  const watchlistIds = useMemo(
    () => watchlistEntries.map((entry) => entry.listingId),
    [watchlistEntries]
  )

  const searchParams = useMemo(
    () =>
      listingSearchStateToSearchParams({
        ...defaultState,
        id: watchlistIds,
        sortBy: AllListingsSortByEnum.EndTime,
        sortOrder: AllListingsSortOrderEnum.Asc,
      }),
    [watchlistIds]
  )

  const { data: searchData, isLoading: isLoadingSearch } =
    listingSearchQuery.useWithOpts(
      [{ params: searchParams, usePostEndpoint: true, fetchFilters: false }],
      { enabled: !!personId && watchlistIds.length > 0 }
    )

  const items = useMemo(
    () =>
      (searchData?.listings ?? [])
        .slice(0, MAX_DISPLAYED_ITEMS)
        .map(toDropdownItem),
    [searchData]
  )

  return {
    items,
    seeAllHref: `/buy/p/${personId}/watchlist`,
    isLoading: isLoadingEntries || (watchlistIds.length > 0 && isLoadingSearch),
  }
}
